import type { ComponentType } from "react";
import { ShieldCheck, Wallet, Tag, Boxes, Package, Box, List, CreditCard, PenTool, FileText as FileTextIcon, BookOpen, CalendarCheck, LayoutGrid, Truck, Users2, Lock, UserPlus, Kanban, FileCheck, MessageCircle, Activity, Users, Building2, Sparkles, Compass, CalendarDays, CalendarClock, LogOut, Paperclip, Hash, SlidersHorizontal, ClipboardList, Gift, Contact } from "bizpandit-ui";

export const ROUTES = {
  login: "/login",
  register: "/register",
  Services: "/services",
  items: "/items",
  packages: "/packages",
  pricing: "/pricing",
  home: "/home",
  dashboard: "/dashboard",   // redirects to /home — the Owner View is folded into it
  event: "/event-mode",
  video: "/videoAssesment",
  lead_home: "/ld/",
  approval: "/approval",
  packageApproval: "/package-approval",
  lov: "/lov",
  lovNew: "/lov/new",
  lovValues: "/lov/:lovId",
  lovEdit: "/lov/:lovId/edit",
  /** Settings — seven sections, the section in the path. */
  settings: "/settings",
  settingsSection: "/settings/:section",
  /** Where /followup-settings lands. V2 made Tags & cadence the first section. */
  settingsFollowUpRules: "/settings/tags",
  /** Superseded by Settings §1 on 2026-08-11. Redirects; kept for bookmarks. */
  followupSettings: "/followup-settings",
  payments: "/payments",
  composer: "/composer",
  remedyCatalog: "/remedy-catalog",
  reports: "/reports",
  /** Access Control console (RBAC v2). Replaces /role-permissions. */
  access: "/access",
  /** Kept so existing links and bookmarks still resolve — redirects to /access. */
  rolePermissions: "/role-permissions",
  auditLog: "/audit-log",
  jobHealth: "/job-health",
  /**
   * Team & Security — the prototype's actual page name, not a dropdown
   * label. Landing tab is the follow-up compliance overview (escalation
   * threshold + by-rep/by-lead missed-followups); Access Control, Audit Log
   * and Report Limits are the other three tabs, still their own routes.
   */
  teamSecurity: "/team-security",
  preSales: "/pre-sales",
  preSalesLeads: "/pre-sales/leads",
  preSalesLeadsNew: "/pre-sales/leads/new",
  preSalesPipeline: "/pre-sales/pipeline",
  /**
   * Retired with the standalone screens they served. Kept only so an old
   * bookmark redirects instead of hitting a blank route — nothing links here.
   * Interactions are logged in the drawer that opens from a lead; follow-ups
   * are worked from the dashboard queue on the day they are due.
   */
  preSalesFollowups: "/pre-sales/followups",
  preSalesInteractions: "/pre-sales/interactions",
  preSalesProposals: "/pre-sales/proposals",
  preSalesWhatsapp: "/pre-sales/whatsapp",
  /** Intake Review — every public door and every possible duplicate (unified requirements A2). */
  preSalesIntakeReview: "/pre-sales/intake-review",
  preSalesReferrals: "/pre-sales/referrals",
  postSales: "/post-sales",
  postSalesClients: "/post-sales/clients",
  postSalesClientsNew: "/post-sales/clients/new",
  postSalesDelivery: "/post-sales/delivery",
  postSalesDeliveryProject: "/post-sales/delivery/:id",
  postSalesFollowups: "/post-sales/followups",
  postSalesClientProfile: "/post-sales/client-profile",
  postSalesCompanies: "/post-sales/companies",
  postSalesCompany: "/post-sales/companies/:id",
  postSalesClient: "/post-sales/clients/:contactId",
  postSalesRenewals: "/post-sales/renewals",
  // BP-ISS-20261002-103 — renewals and additional purchases by existing clients.
  postSalesNewInterests: "/post-sales/new-interests",
  postSalesCommunity: "/post-sales/community",
  postSalesBizcards: "/post-sales/bizcards", // moved to Admin 2026-09-27 — redirects
  adminBizcards: "/admin/bizcards",
  portal: "/portal",
  portalReports: "/portal/reports",
  portalProfile: "/portal/profile",
  portalPayments: "/portal/payments",
  portalCommunity: "/portal/community",
  portalCommunityApply: "/portal/community/apply",
  portalCommunityChapter: "/portal/community/chapter",
  portalCommunityMeetings: "/portal/community/meetings",
  portalCommunityBusiness: "/portal/community/business",
  portalCommunityFeed: "/portal/community/feed",
  portalCommunityBoard: "/portal/community/board",
  portalCommunityWorkshops: "/portal/community/workshops",
  portalCommunityAnalytics: "/portal/community/analytics",
  portalWorkshopCertificate: "/portal/community/workshops/:id/certificate",
  /** Google / Microsoft return here after consent (the redirect URI set with each provider). */
  calendarOAuthReturn: "/calendar/oauth/:provider",
  portalBizcalendar: "/portal/bizcalendar",
  portalRemedies: "/portal/remedies",
  portalDasha: "/portal/dasha",
  portalAstroUpdates: "/portal/astro-updates",
  // Added for Refer & Earn + Numerology Center (our additions).
  portalReferAndEarn: "/portal/refer",
  portalNumerology: "/portal/numerology",
  portalPackages: "/portal/packages",
  portalPlans: "/portal/plans",
  portalLedger: "/portal/ledger",
  portalProjects: "/portal/projects",
  portalBizcard: "/portal/bizcard",
  portalAccountSettings: "/portal/account",
  // Reachable only from the body-text link on Account Settings. Not in the
  // navigation and not in search — 07-ACCOUNT-LIFECYCLE.md §3.1.
  portalEraseData: "/portal/account/erase",
  reportLimits: "/report-limits",
  pendingApproval: "/pending-approval",
  confirmEmailChange: "/confirm-email-change",
  bizcalendarReview: "/bizcalendar-review",
  bizcalendarReviewDetail: "/bizcalendar-review/:id",
  // The Astrology workspace — spec §1.2. The old paths (/bizcalendar-review,
  // /reports, /composer, /remedy-catalog) redirect here for a release;
  // people have them bookmarked.
  astrologyKundali: "/astrology/kundali",
  astrologyKundaliContact: "/astrology/kundali/:contactId",
  astrologyWorkbench: "/astrology/workbench",
  // HR workspace — phase 1 (employee-hr-payroll-design.md v9, hr-module-screen-spec.md Part C).
  hr:          "/hr",
  hrDirectory: "/hr/directory",
  hrEmployee:  "/hr/directory/:employeeId",
  hrSettings:  "/hr/settings",
  hrHiring:     "/hr/hiring",
  hrOnboarding: "/hr/onboarding",
  hrAttendance: "/hr/attendance",
  hrShifts:     "/hr/shifts",
  hrApprovals:  "/hr/approvals",
  hrLeave:      "/hr/leave",
  hrReconciliation: "/hr/reconciliation",
  hrPayroll: "/hr/payroll",
  hrPulse: "/hr/pulse",
  hrCalendar: "/hr/calendar",
  hrSeparation: "/hr/separation",
  adminAttachments: "/admin/attachments",
  // The task engine (Dashboard §6.4: the Staff Management Portal, under Admin).
  adminTasks:     "/tasks",
  adminTaskTypes: "/tasks/types",
  adminPerformance: "/tasks/performance",
  astrologyEventChart: "/astrology/workbench/events/:eventId",
  /**
   * The public consent page (spec §5.3). Outside the app's auth entirely — it
   * is opened from a WhatsApp link by somebody who has no account and never
   * will, so it sits with the other token-driven public routes rather than in
   * the astrology workspace it belongs to conceptually.
   */
  walkInConsent: "/consent/walk-in/:token",
  /** New-hire self-service intake link (BP-ISS-20260919-076) — fully public, token-driven. */
  employeeIntake: "/intake/employee/:token",
  /** Decision #39 — the link in every notice email. Fully public. */
  emailPreferences: "/email-preferences/:token",
  publicAvailability: "/availability/:slug",
  astrologyBizcalendars: "/astrology/bizcalendars",
  astrologyReview: "/astrology/review",
  astrologyReviewDetail: "/astrology/review/:id",
  astrologyReports: "/astrology/reports",
  astrologyAstroUpdates: "/astrology/astro-updates",
  astrologyRemedyCatalogue: "/astrology/remedy-catalogue",
  astrologyNumerology: "/astrology/numerology",
  astrologyRules: "/astrology/rules",
  // Clients who registered before verification was required land here
  // via AuthGuard and cannot leave until the number is confirmed.
  verifyPhone: "/verify-phone",
  forgotPassword: "/forgot-password",
  /** Invite link target — an admin creates the account, the person sets
   *  their own password here. The token in the query string is the credential. */
  setPassword: "/set-password",
  resetPassword: "/reset-password",
} as const;

type IconType = ComponentType<{ className?: string }>;

export interface NavItem {
  label: string;
  href: string;
  icon: IconType;
}

export interface NavSection {
  kind: "section";
  label: string;
  icon: IconType;
  items: NavItem[];
}

export interface NavLeaf extends NavItem {
  kind: "item";
}

export type NavEntry = NavLeaf | NavSection;

export const APP_NAV_ITEMS: NavItem[] = [
  { label: "Services", href: ROUTES.Services, icon: Package },
  { label: "Items", href: ROUTES.items, icon: Box },
  { label: "Package Approvals", href: ROUTES.packageApproval, icon: Wallet },
  { label: "LOV Manager", href: ROUTES.lov, icon: List },
  { label: "Packages", href: ROUTES.packages, icon: Boxes },
  { label: "Pricing", href: ROUTES.pricing, icon: Tag },
  { label: "Settings", href: ROUTES.settings, icon: SlidersHorizontal },
  { label: "Payments", href: ROUTES.payments, icon: CreditCard },
  { label: "Composer", href: ROUTES.composer, icon: PenTool },
  { label: "Reports", href: ROUTES.reports, icon: FileTextIcon },
  { label: "Access Control", href: ROUTES.access, icon: Lock },
];

/**
 * The astrology entries left Admin for their own workspace (spec §1.1) —
 * Remedy Catalog, Composer, BizCalendar Review and Reports live there now.
 * Their old routes still work as redirects for a release, but the nav names
 * one home per screen.
 */
/**
 * Admin, matching the prototype's six groups: Owner View · Catalogue ·
 * Approvals · Payments · Settings · Team & Security.
 *
 * **Catalogue** is a single flat nav entry (not a dropdown) — Services,
 * Items, Packages and Pricing are still four separate routes/pages (see
 * CatalogueTabs.tsx), but the sidebar just links to the first one; the tab
 * strip at the top of each page is how you move between the other three,
 * matching the prototype's one-screen-four-tabs read.
 *
 * **Owner View** is a real page now (features/dashboard), redesigned to
 * match the prototype's macro KPI + bar-chart layout — see DashboardPage.tsx
 * for which numbers are real vs. composed/demo pending an aggregate
 * dashboard endpoint.
 *
 * Two into Approvals, two into Team & Security (plus a new "By rep / by
 * lead" missed-follow-ups view — Demo, no backend aggregate for it yet), and
 * LOV Manager, Follow-up Settings and Report Limits stayed sections of
 * Settings.
 */
export const ADMIN_NAV: NavEntry[] = [
  { kind: "item", label: "Catalogue", href: ROUTES.Services, icon: Boxes },
  {
    kind: "section",
    label: "Approvals",
    icon: ShieldCheck,
    items: [
      { label: "Package Approvals", href: ROUTES.packageApproval, icon: Wallet },
    ],
  },
  { kind: "item", label: "Payments", href: ROUTES.payments, icon: CreditCard },
  // BizCard (Part B): every card, its limits, suspend; the template catalogue.
  // In Admin, not Post-Sales (owner, 2026-09-27). Clients use My BizCard in the portal.
  { kind: "item", label: "BizCards", href: ROUTES.adminBizcards, icon: Contact },
  // The Staff Management Portal (Dashboard §6.4) — tasks and the lists they
  // draw from. Staff Directory itself lives in the HR workspace.
  {
    kind: "section",
    label: "Staff & Tasks",
    icon: Kanban,
    items: [
      { label: "Tasks",              href: ROUTES.adminTasks,     icon: Kanban },
      { label: "Task Types & Lists", href: ROUTES.adminTaskTypes, icon: List },
      { label: "Task Attachments",   href: ROUTES.adminAttachments, icon: Paperclip },
      { label: "Performance",        href: ROUTES.adminPerformance, icon: Activity },
    ],
  },
  // One entry, seven sections. Follow-up rules, business calendar,
  // notifications, lists, modules & plan, integrations, branding.
  { kind: "item", label: "Settings", href: ROUTES.settings, icon: SlidersHorizontal },
  { kind: "item", label: "Team & Security", href: ROUTES.teamSecurity, icon: Lock },
];

/** Back-compat alias — prefer ADMIN_NAV in new code. */
export const APP_NAV = ADMIN_NAV;

/**
 * Two sales workspaces, Pre-Sales and Post-Sales (owner, 2026-09-15: "Divide
 * CRM in Pre sales and Post sales"). This reverses the single process-wise
 * CRM rail of 2026-09-11; the split is the one the /pre-sales/* and
 * /post-sales/* URLs already carry, so nothing moves. Who sees which entry
 * is still decided per screen by its object grant (useNavAccess.filterEntries).
 *
 * Still NO "Follow-ups" and NO "Interactions" entry, deliberately: a
 * follow-up is worked from the dashboard queue on the day it is due, and an
 * interaction is LOGGED about a record in the drawer that opens from it. Two
 * standalone lists asked the rep to navigate to work they had already been
 * shown.
 */
export const PRE_SALES_NAV: NavEntry[] = [
  { kind: "item", label: "Leads",           href: ROUTES.preSalesLeads,     icon: UserPlus },
  // Beside Leads (unified requirements F4): the one queue for public doors,
  // possible duplicates and portal signups. Named so because Astrology
  // already has a "Review Queue" for BizCalendars.
  { kind: "item", label: "Intake Review",   href: ROUTES.preSalesIntakeReview, icon: ClipboardList },
  { kind: "item", label: "Pipeline",        href: ROUTES.preSalesPipeline,  icon: Kanban },
  { kind: "item", label: "Proposals",       href: ROUTES.preSalesProposals, icon: FileCheck },
  // Moved from Team & Security → Report Limits (owner, 2026-09-24): the Referral ledger.
  { kind: "item", label: "Referrals",       href: ROUTES.preSalesReferrals, icon: Gift },
  // One-to-one subscriber messaging (BP-ISS-20260917-055); groups cannot be automated on Meta's platform.
  { kind: "item", label: "WhatsApp Subscribers", href: ROUTES.preSalesWhatsapp,  icon: MessageCircle },
];

export const POST_SALES_NAV: NavEntry[] = [
  { kind: "item", label: "Clients",         href: ROUTES.postSalesClients,  icon: Users },
  { kind: "item", label: "Companies",       href: ROUTES.postSalesCompanies,icon: Building2 },
  { kind: "item", label: "Delivery",        href: ROUTES.postSalesDelivery, icon: Truck },
  { kind: "item", label: "Renewals",        href: ROUTES.postSalesRenewals, icon: CalendarCheck },
  // Renewals, upsells and add-ons an existing client is considering (BP-ISS-20261002-103).
  { kind: "item", label: "New interests",   href: ROUTES.postSalesNewInterests, icon: Gift },
  // Community (Part E): membership screening and members; chapters and matching join in later steps.
  { kind: "item", label: "Community",       href: ROUTES.postSalesCommunity, icon: Users2 },
];

/**
 * The Astrology workspace — spec §1.1, in its order. Remedy Catalogue is
 * nested under Astro Updates in the spec's tree; the flat nav renders it as
 * the sub-entry it is.
 */
export const ASTROLOGY_NAV: NavEntry[] = [
  { kind: "item", label: "Kundali",           href: ROUTES.astrologyKundali,        icon: Sparkles },
  { kind: "item", label: "Kundali Workbench", href: ROUTES.astrologyWorkbench,      icon: Compass },
  { kind: "item", label: "BizCalendars",      href: ROUTES.astrologyBizcalendars,   icon: CalendarDays },
  { kind: "item", label: "Review Queue",      href: ROUTES.astrologyReview,         icon: CalendarCheck },
  { kind: "item", label: "Reports",           href: ROUTES.astrologyReports,        icon: FileTextIcon },
  { kind: "item", label: "Astro Updates",     href: ROUTES.astrologyAstroUpdates,   icon: PenTool },
  { kind: "item", label: "Remedy Catalogue",  href: ROUTES.astrologyRemedyCatalogue, icon: BookOpen },
  { kind: "item", label: "Numerology",        href: ROUTES.astrologyNumerology,     icon: Hash },
  { kind: "item", label: "Rules & Reference", href: ROUTES.astrologyRules,          icon: SlidersHorizontal },
];

export type Workspace = "admin" | "presales" | "postsales" | "astrology" | "hr";

export interface WorkspaceMeta {
  id: Workspace;
  label: string;
  homeRoute: string;
  nav: NavEntry[];
  icon: IconType;
}

/**
 * The HR workspace — screen spec Part C, in build order. Phase 1 ships the
 * three screens everything else reads; the rest of the mockup's rail arrives
 * with its phase and is NOT listed until it exists, so the nav never
 * advertises a door that opens onto nothing.
 */
export const HR_NAV: NavEntry[] = [
  { kind: "item", label: "Hiring",          href: ROUTES.hrHiring,     icon: UserPlus },
  { kind: "item", label: "Onboarding",      href: ROUTES.hrOnboarding, icon: FileCheck },
  { kind: "item", label: "Staff Directory", href: ROUTES.hrDirectory, icon: Users2 },
  { kind: "item", label: "Attendance",      href: ROUTES.hrAttendance, icon: CalendarCheck },
  { kind: "item", label: "Shifts",          href: ROUTES.hrShifts,     icon: LayoutGrid },
  { kind: "item", label: "Shift Reconciliation", href: ROUTES.hrReconciliation, icon: Lock },
  { kind: "item", label: "Leave",           href: ROUTES.hrLeave,      icon: CalendarDays },
  { kind: "item", label: "Approvals",       href: ROUTES.hrApprovals,  icon: ShieldCheck },
  { kind: "item", label: "Payroll",         href: ROUTES.hrPayroll,    icon: Wallet },
  { kind: "item", label: "Monthly Pulse",   href: ROUTES.hrPulse,      icon: Activity },
  { kind: "item", label: "Calendar",        href: ROUTES.hrCalendar,   icon: CalendarClock },
  { kind: "item", label: "Separation",      href: ROUTES.hrSeparation, icon: LogOut },
  { kind: "item", label: "HR Settings",     href: ROUTES.hrSettings,  icon: SlidersHorizontal },
];

export const WORKSPACES: WorkspaceMeta[] = [
  { id: "astrology",  label: "Astrology",  homeRoute: ROUTES.astrologyKundali, nav: ASTROLOGY_NAV, icon: Sparkles },
  { id: "admin",      label: "Admin",      homeRoute: ROUTES.Services,      nav: ADMIN_NAV, icon: LayoutGrid },
  { id: "presales",   label: "Pre-Sales",  homeRoute: ROUTES.preSalesLeads,   nav: PRE_SALES_NAV,  icon: UserPlus },
  { id: "postsales",  label: "Post-Sales", homeRoute: ROUTES.postSalesClients, nav: POST_SALES_NAV, icon: Users },
  // Staff Directory, not /hr: /hr redirects to Home since 2026-09-10, and a
  // workspace whose entry point leaves the workspace shows no rail at all.
  { id: "hr",         label: "HR",         homeRoute: ROUTES.hrDirectory,   nav: HR_NAV,    icon: Users2 },
];

export function getAvailableWorkspaces(role?: string): WorkspaceMeta[] {
  const internalRoles = ["ADMIN", "SUPER_ADMIN", "STAFF", "admin", "manager", "staff"];
  if (!role || !internalRoles.includes(role)) return [];
  return WORKSPACES;
}

export const APP_NAME = "BizPandit";
